import { MouseEventHandler, useContext, useEffect } from 'react';
import { addDays, format, isSameDay } from 'date-fns';
import useOpenJobModal from 'components/modal/jobModal/useOpenJobModal';
import useLeadsOverlapModal from 'components/domain/lead/overlap/useLeadsOverlapModal';
import useAppUser from 'hooks/useAppUser';
import { StackedCalendarMode } from 'pages/virtualizedStackedCalendar/Calendar.types';
import CalendarContext from 'pages/virtualizedStackedCalendar/CalendarContext';
import useStackedCalendarVisibleProperties from 'pages/virtualizedStackedCalendar/useStackedCalendarVisibleProperties';
import { getLeadsOverlappingElement } from 'pages/calendar/Calendar.utils';
import { getLeadCheckInDate, getLeadCheckOutDate } from 'utils/lead/leadUtils';
import useCalendarMode from 'pages/virtualizedStackedCalendar/useCalendarMode';
import useCalendarOpenLeadModal from 'pages/calendar/common/lead/useCalendarOpenLeadModal';
import useCalendarOpenPricingModal from 'pages/calendar/common/pricing/useCalendarOpenPricingModal';
import useCalendarBodySelectionAllowed from 'pages/calendar/common/body/selection/useCalendarBodySelectionAllowed';
import useFeatures from 'hooks/useFeatures';
import useCalendarBodySelection from './useCalendarBodySelection';
import { CalendarSelectionLimitProvider } from './CalendaBodySelection.types';

// @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
function isInactiveDayCell(event) {
  return event.target.closest('div.body-inactive-day-cell');
}

// @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
function getJobItem(event) {
  return event.target.closest('span[data-job-uid]');
}

// @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
function getLeadItem(event) {
  return event.target.closest('div[data-lead-uid]');
}

const useCalendarBodyMouseEvents = (
  selectionLimitProvider: CalendarSelectionLimitProvider,
) => {
  const { isOwner } = useAppUser();
  const { allowCalendarPropertiesOptimization: isPropertiesOptimized } =
    useFeatures();
  const { resetSelection, handleMouseDown, handleMouseUp } =
    useCalendarBodySelection();
  const selectionAllowed = useCalendarBodySelectionAllowed();
  // Optimized: modals self-fetch the full list on open. Master: pass the eager full list.
  const { openEditLeadModal, openCreateLeadModal } = useCalendarOpenLeadModal({
    selfFetchProperties: isPropertiesOptimized,
  });
  const { openPropertyPricingModal } = useCalendarOpenPricingModal();
  const { jobsMap, allProperties, leadsMap, selectedProperty, daysLeadsMap } =
    useContext(CalendarContext);
  const { updateJobModal } = useOpenJobModal(
    isPropertiesOptimized
      ? { jobsMap, fetchPropertiesWhenEmpty: false }
      : { jobsMap, properties: allProperties },
  );
  const { isPricingMode, isBookingMode, isCombinedMode } = useCalendarMode();
  const { openLeadsOverlapModal } = useLeadsOverlapModal();
  const isOperatingAsBookingMode = isBookingMode || isCombinedMode;
  const visibleProperties = useStackedCalendarVisibleProperties();

  useEffect(() => {
    const keydownHandler = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        resetSelection();
      }
    };

    document.addEventListener('keydown', keydownHandler);

    return () => {
      document.removeEventListener('keydown', keydownHandler);
    };
  }, [resetSelection]);

  // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const onMouseDown = (event) => {
    if (
      !selectionAllowed ||
      isInactiveDayCell(event) ||
      (isOperatingAsBookingMode && (getJobItem(event) || getLeadItem(event)))
    ) {
      return;
    }

    const bodyCell = event.target.closest('div.body-day-cell');
    if (bodyCell) {
      const {
        dataset: { dayDate, propertyUid },
      } = bodyCell;
      const date = new Date(Number(dayDate));
      const isUnitType = bodyCell.dataset.isUnitType === 'true';
      const isUnit = bodyCell.dataset.isUnit === 'true';

      const limit = selectionLimitProvider(date, propertyUid);
      if (
        (isUnitType && isOperatingAsBookingMode) ||
        (isUnit && isPricingMode)
      ) {
        return;
      }
      // no limit means there is no room for creating a selection
      if (limit) {
        handleMouseDown({
          min: date,
          max: isPricingMode ? date : addDays(date, 1),
          propertyUid,
          limit,
          mode: isPricingMode
            ? StackedCalendarMode.PRICING
            : StackedCalendarMode.BOOKING,
          visibleProperties,
        });
      }
    }
  };

  const onMouseUp: MouseEventHandler<HTMLDivElement> = (event) => {
    // this is so we can "hover" using rightclick in the LeadView to show the tooltip, in Cypress:
    if (typeof Cypress !== 'undefined' && event.button === 2) {
      return;
    }

    if (isInactiveDayCell(event)) {
      return;
    }

    const jobItem = getJobItem(event);
    if (isOperatingAsBookingMode && jobItem && !isOwner) {
      const jobUids: string[] = jobItem
        .getAttribute('data-job-uid')
        .split('--\u200c--');
      updateJobModal(jobUids);
      return;
    }

    const leadItem = getLeadItem(event);

    if (isOperatingAsBookingMode && leadItem) {
      const isStandaloneCalendar = !!selectedProperty;

      if (isStandaloneCalendar) {
        const leadUid = leadItem.getAttribute('data-lead-uid');
        const lead = leadsMap[leadUid];
        const checkIn = getLeadCheckInDate(lead);
        const checkOut = getLeadCheckOutDate(lead);

        const leadsOverlap: string[] = [];

        for (let day = checkIn; day <= checkOut; day = addDays(day, 1)) {
          const leadsOfThisDay =
            daysLeadsMap.current[format(day, 'yyyy-MM-dd')];

          if (leadsOfThisDay && leadsOfThisDay.length > 1) {
            leadsOfThisDay.forEach((l) => {
              const isCurrentLeadClicked = l.uid === leadUid;

              if (!isCurrentLeadClicked) {
                const otherLead = l;
                const {
                  checkIn: otherLeadCheckIn,
                  checkOut: otherLeadCheckOut,
                } = otherLead;

                if (!leadsOverlap.includes(otherLead.uid)) {
                  if (
                    !isSameDay(otherLeadCheckIn, checkOut) &&
                    !isSameDay(otherLeadCheckOut, checkIn)
                  ) {
                    leadsOverlap.push(l.uid);
                  }
                }
              }
            });
          }
        }

        if (leadsOverlap.length > 0) {
          // Optimized: pass no list → block modal self-fetches on open. Master: full list.
          openLeadsOverlapModal(
            [...leadsOverlap.map((p) => leadsMap[p]), lead],
            isPropertiesOptimized ? undefined : allProperties,
          );
          return;
        }
      }

      const bodyDayCellLeads = getLeadsOverlappingElement(
        event.target as HTMLDivElement,
      );

      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      if (bodyDayCellLeads?.length > 1 && !isStandaloneCalendar) {
        // @ts-expect-error TS2769 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        const leads = Array.from(bodyDayCellLeads).map(
          // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          (child) => leadsMap[child.getAttribute('data-lead-uid')],
        );
        openLeadsOverlapModal(
          leads,
          isPropertiesOptimized ? undefined : allProperties,
        );
        return;
      }

      if (leadItem.getAttribute('data-main-booking-uid')) {
        openEditLeadModal(leadItem.getAttribute('data-main-booking-uid'));
      } else {
        openEditLeadModal(leadItem.getAttribute('data-lead-uid'));
      }

      resetSelection();
      return;
    }

    const bodyCell = (event.target as HTMLDivElement).closest(
      'div.body-day-cell',
    ) as HTMLDivElement;

    if (bodyCell) {
      const {
        dataset: { dayDate },
      } = bodyCell;
      const date = new Date(Number(dayDate));
      const isUnitType = bodyCell.dataset.isUnitType === 'true';
      const isUnit = bodyCell.dataset.isUnit === 'true';

      if (
        (isUnitType && isOperatingAsBookingMode) ||
        (isUnit && isPricingMode)
      ) {
        return;
      }

      handleMouseUp({
        actionCallback: (selectionData) => {
          if (isOperatingAsBookingMode) {
            resetSelection();
            openCreateLeadModal({
              propertyUid: selectionData.propertyUids[0],
              dateFrom: selectionData.from,
              dateTo: selectionData.to,
            });
          } else {
            openPropertyPricingModal({
              propertyUids: selectionData.propertyUids,
              dateFrom: selectionData.from,
              dateTo: selectionData.to,
            });
          }
        },
        date,
      });
    }
  };

  return { onMouseDown, onMouseUp };
};

export default useCalendarBodyMouseEvents;
